import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  CalendarDays,
  DollarSign,
  RefreshCw,
  Users,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";

const RANGE_OPTIONS = [
  { key: "24h", label: "24h" },
  { key: "today", label: "Today" },
  { key: "7d", label: "7d" },
  { key: "month", label: "Month" },
  { key: "90d", label: "90d" },
  { key: "1y", label: "1y" },
];

const TOP_MODES = [
  { key: "deposits", label: "By Deposits" },
  { key: "net_to_house", label: "By Net to House" },
  { key: "withdrawals", label: "By Withdrawals" },
];

const TOPUP_COLORS = ["bg-emerald-400", "bg-amber-400", "bg-sky-400", "bg-fuchsia-400", "bg-slate-400"];

export default function AdminAnalytics() {
  const [rangeFilter, setRangeFilter] = useState("24h");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topMode, setTopMode] = useState("deposits");

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ range_filter: rangeFilter });
      if (startDate) params.set("start", startDate);
      if (endDate) params.set("end", endDate);
      const response = await api.get(`/admin/analytics?${params.toString()}`);
      setData(response.data || {});
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [endDate, rangeFilter, startDate]);

  useEffect(() => {
    load();
  }, [load]);

  useAdminAutoRefresh(() => load({ quiet: true }));

  const stats = data?.stats || {};
  const portfolio = data?.wallet_portfolio || {};
  const trend = data?.trend || { points: [], totals: {} };
  const sources = data?.topup_sources || [];
  const perGame = data?.per_game || [];
  const topMembers = useMemo(() => {
    return [...(data?.top_members || [])].sort((a, b) => Number(b[topMode] || 0) - Number(a[topMode] || 0));
  }, [data?.top_members, topMode]);

  const rangeLabel = useMemo(() => {
    if (startDate || endDate) return "custom range";
    return RANGE_OPTIONS.find((item) => item.key === rangeFilter)?.label || "24h";
  }, [endDate, rangeFilter, startDate]);

  return (
    <div className="max-w-full overflow-x-hidden p-3 pb-28 sm:p-6 md:p-8">
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Analytics</h1>
          <p className="mt-1 text-sm text-slate-400">Wallet, dollars, deposits & withdrawals - {rangeLabel}.</p>
        </div>
        <div className="flex max-w-full flex-wrap gap-2">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => {
                setRangeFilter(option.key);
                setStartDate("");
                setEndDate("");
              }}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                rangeFilter === option.key && !startDate && !endDate
                  ? "border-amber-400 bg-amber-400 text-black"
                  : "border-white/10 bg-[#101012] text-slate-100 hover:border-amber-400/50"
              }`}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="rounded-full border border-white/10 bg-[#101012] p-2 text-slate-100 hover:border-amber-400/50"
            aria-label="Refresh analytics"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-red-500/30 bg-red-950/40 px-4 py-3 text-sm font-semibold text-red-200">
          {error}
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <MetricCard icon={ArrowDownToLine} label="Player deposits" value={formatMoney(stats.player_deposits)} tone="gold" />
        <MetricCard icon={ArrowUpFromLine} label="Player withdrawals" value={formatMoney(stats.player_withdrawals)} />
        <MetricCard icon={DollarSign} label="Net to house" value={formatMoney(stats.net_to_house)} tone="gold" />
        <MetricCard icon={Activity} label="Active players (7d)" value={stats.active_players_7d || 0} />
        <MetricCard icon={Users} label="New members" value={stats.new_members || 0} />
        <MetricCard icon={Users} label="Total members" value={stats.total_members || 0} />
      </div>

      <div className="mb-6 grid gap-4 xl:grid-cols-[2fr_1fr]">
        <section className="rounded-xl border border-amber-500/20 bg-[#121214] p-5 sm:p-6">
          <h2 className="mb-5 text-lg font-bold uppercase tracking-wider text-slate-400">Wallet Portfolio</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <PortfolioItem label="Current balance" value={formatMoney(portfolio.current_balance)} tone="gold" />
            <PortfolioItem label="Total topped-up" value={formatMoney(portfolio.total_topped_up)} detail={`${portfolio.total_topups || 0} topups`} />
            <PortfolioItem label="Dollars refilled" value={formatMoney(portfolio.coins_refilled)} detail={`${portfolio.refill_count || 0} refills`} />
            <PortfolioItem label="Dollars in pool" value={formatMoney(portfolio.coins_in_pool)} />
            <PortfolioItem label="Pending topups" value={portfolio.pending_topups || 0} />
            <PortfolioItem label="Pending withdrawals" value={portfolio.pending_withdrawals || 0} />
          </div>
        </section>

        <section className="rounded-xl border border-amber-500/20 bg-[#121214] p-5 sm:p-6">
          <h2 className="mb-5 text-lg font-bold uppercase tracking-wider text-slate-400">Topup Source</h2>
          {sources.length > 0 ? (
            <div className="space-y-4">
              {sources.slice(0, 5).map((source, index) => (
                <div key={source.source || index}>
                  <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                    <span className="font-semibold text-white">{source.source || "Manual"}</span>
                    <span className="text-slate-400">{formatMoney(source.amount)} / {source.count || 0}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/10">
                    <div
                      className={`h-full ${TOPUP_COLORS[index % TOPUP_COLORS.length]}`}
                      style={{ width: `${Math.min(Number(source.percent || 0), 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-sm text-slate-400">No approved topups in this period.</p>
          )}
        </section>
      </div>

      <section className="mb-6 rounded-xl border border-amber-500/20 bg-[#121214] p-5 sm:p-6">
        <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h2 className="text-lg font-bold uppercase tracking-wider text-slate-400">Player Money Trend - {trend.bucket === "day" ? "Daily" : "Hourly"}</h2>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <LegendDot color="bg-emerald-400" label="Deposits" value={formatMoney(trend.totals?.deposits)} />
              <LegendDot color="bg-rose-400" label="Withdrawals" value={formatMoney(trend.totals?.withdrawals)} />
              <LegendDot color="bg-amber-400" label="Revenue" value={formatMoney(trend.totals?.revenue)} />
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <DateInput value={startDate} onChange={setStartDate} />
            <DateInput value={endDate} onChange={setEndDate} />
          </div>
        </div>
        <MoneyTrendChart points={trend.points || []} />
      </section>

      <section className="mb-6 rounded-xl border border-amber-500/20 bg-[#121214] p-5 sm:p-6">
        <h2 className="mb-5 text-lg font-bold uppercase tracking-wider text-slate-400">Per-Game Dollar Activity</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
              <tr className="border-b border-amber-500/20">
                <th className="py-3 pr-4">Game</th>
                <th className="px-4 py-3 text-right">Refilled</th>
                <th className="px-4 py-3 text-right">Pool balance</th>
                <th className="py-3 pl-4 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {perGame.map((row) => (
                <tr key={row.gameId || row.game} className="border-b border-white/5 last:border-0">
                  <td className="py-4 pr-4 font-semibold text-white">
                    {row.game || row.gameId}
                    <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{row.load_count || 0} loads</div>
                  </td>
                  <td className="px-4 py-4 text-right font-mono text-amber-300">{formatMoney(row.refilled)}</td>
                  <td className="px-4 py-4 text-right font-mono text-white">{formatMoney(row.pool_balance)}</td>
                  <td className="py-4 pl-4 text-right">
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                      row.ok === false ? "border-red-500/30 text-red-200" : "border-emerald-500/30 text-emerald-300"
                    }`}>
                      {row.ok === false ? "Needs attention" : "Live"}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && perGame.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-sm text-slate-500">
                    No game activity found for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-amber-500/20 bg-[#121214] p-5 sm:p-6">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-lg font-bold uppercase tracking-wider text-slate-400">Top Members</h2>
          <div className="flex flex-wrap gap-2">
            {TOP_MODES.map((mode) => (
              <button
                key={mode.key}
                type="button"
                onClick={() => setTopMode(mode.key)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold ${
                  topMode === mode.key
                    ? "border-amber-400 bg-amber-400 text-black"
                    : "border-white/10 bg-[#101012] text-white hover:border-amber-400/50"
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
              <tr className="border-b border-amber-500/20">
                <th className="py-3 pr-4">#</th>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3 text-right">Deposits</th>
                <th className="px-4 py-3 text-right">Withdrawals</th>
                <th className="py-3 pl-4 text-right">Net to house</th>
              </tr>
            </thead>
            <tbody>
              {topMembers.map((member, index) => (
                <tr key={member.user_id || member.email || member.username || index} className="border-b border-white/5 last:border-0">
                  <td className="py-4 pr-4 font-mono text-amber-300">#{index + 1}</td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-white">{member.username || "Member"}</div>
                    <div className="mt-1 text-xs text-slate-500">{member.email || "-"}</div>
                  </td>
                  <td className="px-4 py-4 text-right font-mono text-amber-300">{formatMoney(member.deposits)}</td>
                  <td className="px-4 py-4 text-right font-mono text-rose-300">{formatMoney(member.withdrawals)}</td>
                  <td className="py-4 pl-4 text-right font-mono text-white">{formatMoney(member.net_to_house)}</td>
                </tr>
              ))}
              {!loading && topMembers.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-sm text-slate-500">
                    No member money activity found for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-amber-500/20 bg-[#121214] p-4">
      <Icon className={`mb-4 h-5 w-5 ${tone === "gold" ? "text-amber-300" : "text-slate-400"}`} />
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`mt-2 text-3xl font-black tabular-nums ${tone === "gold" ? "text-amber-300" : "text-white"}`}>
        {typeof value === "number" ? value.toLocaleString("en-US") : value}
      </div>
    </div>
  );
}

function PortfolioItem({ label, value, detail, tone }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl font-black tabular-nums ${tone === "gold" ? "text-amber-300" : "text-white"}`}>
        {typeof value === "number" ? value.toLocaleString("en-US") : value}
      </div>
      {detail && <div className="mt-1 text-xs text-slate-500">{detail}</div>}
    </div>
  );
}

function DateInput({ value, onChange }) {
  return (
    <label className="relative block">
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full min-w-[180px] rounded-full border border-white/10 bg-[#101012] px-4 pr-10 text-sm text-white outline-none focus:border-amber-400/60"
      />
      <CalendarDays className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </label>
  );
}

function LegendDot({ color, label, value }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`h-3 w-3 rounded-full ${color}`} />
      <span className="font-semibold text-slate-100">{label}</span>
      <span className="font-mono text-amber-300">{value}</span>
    </span>
  );
}

function MoneyTrendChart({ points }) {
  const chart = useMemo(() => buildChart(points), [points]);

  if (!points.length) {
    return (
      <div className="rounded-xl border border-white/5 bg-black/20 py-20 text-center text-sm text-slate-500">
        No trend data in this range.
      </div>
    );
  }

  return (
    <div className="max-w-full overflow-x-auto">
      <svg className="block min-w-[760px]" viewBox="0 0 920 360" role="img" aria-label="Player money trend chart">
        <g>
          {chart.grid.map((line) => (
            <line key={line.y} x1="55" x2="890" y1={line.y} y2={line.y} stroke="rgba(255,255,255,0.08)" strokeDasharray="5 7" />
          ))}
          {chart.grid.map((line) => (
            <text key={line.label} x="15" y={line.y + 5} fill="#93A4B8" fontSize="12">{line.label}</text>
          ))}
        </g>
        <polyline points={chart.deposits} fill="none" stroke="#34D399" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={chart.withdrawals} fill="none" stroke="#FB7185" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={chart.revenue} fill="none" stroke="#FACC15" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {chart.labels.map((item) => (
          <text key={`${item.x}-${item.label}`} x={item.x} y="340" fill="#93C5FD" fontSize="12" textAnchor="middle">{item.label}</text>
        ))}
      </svg>
    </div>
  );
}

function buildChart(points) {
  const width = 835;
  const left = 55;
  const top = 25;
  const height = 280;
  const values = points.flatMap((point) => [
    Number(point.deposits || 0),
    Number(point.withdrawals || 0),
    Number(point.revenue || 0),
  ]);
  const maxAbs = Math.max(1, ...values.map((value) => Math.abs(value)));
  const roundedMax = Math.ceil(maxAbs / 20) * 20 || 20;
  const midY = top + height / 2;
  const xFor = (index) => left + (points.length <= 1 ? 0 : (index / (points.length - 1)) * width);
  const yFor = (value) => midY - (Number(value || 0) / roundedMax) * (height / 2);
  const lineFor = (field) => points.map((point, index) => `${xFor(index).toFixed(2)},${yFor(point[field]).toFixed(2)}`).join(" ");
  const labelEvery = Math.max(1, Math.ceil(points.length / 10));

  return {
    deposits: lineFor("deposits"),
    withdrawals: lineFor("withdrawals"),
    revenue: lineFor("revenue"),
    grid: [-roundedMax, -roundedMax / 2, 0, roundedMax / 2, roundedMax].map((value) => ({
      y: yFor(value),
      label: `${value < 0 ? "-" : ""}$${Math.abs(value).toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
    })),
    labels: points
      .map((point, index) => ({ x: xFor(index), label: point.label, index }))
      .filter((item, index) => index === 0 || index === points.length - 1 || item.index % labelEvery === 0),
  };
}

function formatMoney(value = 0) {
  const number = Number(value) || 0;
  return `$${number.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}
